import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";
import NotificationRecipientSchema, {
  NotificationRecipientJSON,
  NotificationRecipientParams,
} from "notifications/recipients/notification-recipient-schema";

/**
 * Represents a recipient of a notification, including their unique identifier and the timestamp when the notification was seen.
 *
 * Provides factory methods for creation and deserialization, as well as utilities for serialization, equality checks, and string representation.
 *
 * @remarks
 * - Use {@link NotificationRecipient.create} to instantiate from parameters.
 * - Use {@link NotificationRecipient.fromJSON} to instantiate from a JSON object.
 * - The {@link isSeen} property indicates whether the notification has been seen.
 *
 * @example
 * ```typescript
 * const recipient = NotificationRecipient.create({ userId, seenAt: null });
 * if (!recipient.isSeen) {
 *   // handle unseen notification
 * }
 * ```
 */
export default class NotificationRecipient {
  public static create(values: NotificationRecipientParams): NotificationRecipient {
    const parsed = NotificationRecipientSchema.validateJSON(values);
    return new NotificationRecipient(parsed);
  }

  public static fromJSON(value: NotificationRecipientJSON): NotificationRecipient {
    const parsed = NotificationRecipientSchema.deserialize(value);
    return new NotificationRecipient(parsed);
  }

  public readonly userId: Guid;
  public readonly seenAt: AHDate | null;

  public get isSeen(): boolean {
    return this.seenAt !== null;
  }

  public constructor(params: NotificationRecipientParams) {
    const validatedParams = NotificationRecipientSchema.validateJSON(params);
    this.userId = validatedParams.userId;
    this.seenAt = validatedParams.seenAt;
  }

  public toJSON(): NotificationRecipientJSON {
    return NotificationRecipientSchema.serialize(this);
  }

  public isEqualTo(other: NotificationRecipient): boolean {
    return this.userId.isEqualTo(other.userId);
  }

  public toString(): string {
    return `NotificationRecipient { userId: ${this.userId.value}, seenAt: ${this.seenAt?.toISOString() ?? "null"} }`;
  }
}
